import {
  SignupBody,
  SignupResponse,
  useTypeSafeMutation,
} from '@oresundsbron/api';
import { invoiceParser } from '@oresundsbron/validators';
import {
  Box,
  Button,
  LoadingButton,
  Typography,
} from '@oresundsbron/bridge-ui';
import { flow, pipe } from 'fp-ts/lib/function';
import {
  fromNullable,
  fromPredicate,
  getOrElseW,
  map,
  match,
} from 'fp-ts/lib/Option';
import { Lens } from 'monocle-ts';
import { FC, useCallback, useState } from 'react';
import {
  CreditEvalError,
  CREDIT_EVAL_RED,
  CREDIT_EVAL_YELLOW,
  isBackendError,
} from '../../../lib/errors';
import { z } from 'zod';
import { Trans, useTranslation } from 'next-i18next';
import { tapO } from '@oresundsbron/utilities';
import { useRouter } from 'next/router';
import { useSignUpStore, signUpStoreActions } from '@oresundsbron/app-common';

const { setError, setResponse, setPayWithCreditCard, setToken } =
  signUpStoreActions;

type Invoice = z.input<typeof invoiceParser>;

const paymentMethodLens = Lens.fromProp<SignupBody>()('paymentMethod');
const invoiceLens = Lens.fromProp<{ method: 'invoice'; invoice: Invoice }>()(
  'invoice'
);
const ignoreEvaluationLens = Lens.fromProp<Invoice>()('ignoreEvaluation');

const setIgnoreEvalution = paymentMethodLens.modify((p) =>
  pipe(
    p,
    fromPredicate(
      (x): x is { method: 'invoice'; invoice: Invoice } =>
        x?.method === 'invoice'
    ),
    match(() => p, flow(invoiceLens.modify(ignoreEvaluationLens.set('true'))))
  )
);

const onError = (e: unknown) =>
  pipe(
    e,
    fromPredicate(isBackendError),
    match(
      () => {},
      (e) => setError(e)
    )
  );

const onSuccess = (data: SignupResponse) => {
  const response = data as SignupResponse;
  setResponse(response);
  setToken(response.code);
};

export const CreditEvaluationError: FC<{
  status: CreditEvalError;
}> = ({ status }) => {
  const { reload } = useRouter();
  const [loading, toggleLoading] = useState(false);
  const { mutateAsync } = useTypeSafeMutation(
    ['signup'],
    (req) => req.Customer.signup,
    {
      onError,
      onSuccess,
      onSettled: () => toggleLoading(false),
    }
  );

  const { t } = useTranslation(['businessSignUp', 'common'], {
    nsMode: 'fallback',
  });

  const { data } = useSignUpStore();

  const onContinue = useCallback(
    () =>
      pipe(
        data,
        fromNullable,
        map(setIgnoreEvalution),
        tapO(() => toggleLoading(true)),
        map(mutateAsync),
        getOrElseW(() =>
          setError({ code: 500, error: 'no signup data initialized' })
        )
      ),
    [data, mutateAsync]
  );

  const onPayWithCreditCard = useCallback(() => {
    setPayWithCreditCard();
    setError(undefined);
  }, []);

  return (
    <>
      <Typography
        className="col-span-full row-start-1 sm:col-span-6 sm:col-start-2 md:col-start-4"
        as="h1"
        intent="headline"
      >
        {status === CREDIT_EVAL_YELLOW
          ? t('form.sections.creditEvaluation.yellow.title')
          : t('form.sections.creditEvaluation.red.title')}
      </Typography>
      <Box className="col-span-full row-start-2 sm:col-span-6 sm:col-start-2 md:col-start-4">
        <Trans
          ns="businessSignUp"
          i18nKey={
            status === CREDIT_EVAL_YELLOW
              ? 'form.sections.creditEvaluation.yellow.description'
              : 'form.sections.creditEvaluation.red.description'
          }
        >
          <Typography gutter />
        </Trans>
      </Box>
      <Box className="col-span-full row-start-3 flex flex-col sm:col-span-6 sm:col-start-2 sm:flex-row-reverse sm:justify-end md:col-start-4">
        {status === CREDIT_EVAL_YELLOW ? (
          <>
            <LoadingButton
              isLoading={loading}
              loadingLabel={t('action.loading')}
              onClick={onContinue}
              className="ml-auto mr-auto sm:ml-0 sm:mr-0"
            >
              {t('action.continueEvaluation')}
            </LoadingButton>
            <Button
              onClick={onPayWithCreditCard}
              intent="outlined"
              className="ml-auto mr-auto mt-4 sm:mr-8 sm:mt-0"
            >
              {t('action.payWithCard')}
            </Button>
          </>
        ) : null}
        {status === CREDIT_EVAL_RED ? (
          <>
            <Button
              onClick={onPayWithCreditCard}
              className="ml-auto mr-auto sm:ml-0 sm:mr-0"
            >
              {t('action.payWithCard')}
            </Button>

            <Button
              onClick={() => reload()}
              color="negative"
              className="ml-auto mr-auto mt-4 sm:mr-8 sm:mt-0"
            >
              {t('action.cancel')}
            </Button>
          </>
        ) : null}
      </Box>
    </>
  );
};
